/**
 * NotificationCenter Component - Manages and displays all notifications
 */

import { useState, useEffect } from 'react';
import {
  subscribeToNotifications,
  markAllAsRead,
  deleteAllNotifications,
  getUnreadCount
} from '../services/notificationService';
import Notification from './Notification';
import '../styles/NotificationCenter.css';

const NotificationCenter = ({ userId, currentUser }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  useEffect(() => {
    if (!userId) return;

    // Subscribe to real-time notifications
    const unsubscribe = subscribeToNotifications(userId, (updatedNotifications) => {
      setNotifications(updatedNotifications);

      // Update unread count
      const unread = updatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    });

    return () => unsubscribe();
  }, [userId]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    setShowClearConfirm(false);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead(userId);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (!showClearConfirm) {
      setShowClearConfirm(true);
      return;
    }

    try {
      await deleteAllNotifications(userId);
      setNotifications([]);
      setUnreadCount(0);
      setShowClearConfirm(false);
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  const handleNotificationDismiss = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const notificationCenter = document.querySelector('.notification-center');
      const notificationPanel = document.querySelector('.notification-panel');

      if (
        notificationCenter &&
        notificationPanel &&
        !notificationCenter.contains(e.target) &&
        !notificationPanel.contains(e.target)
      ) {
        setIsOpen(false);
        setShowClearConfirm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const unreadNotifications = notifications.filter(n => !n.read);
  const readNotifications = notifications.filter(n => n.read);

  return (
    <div className="notification-center">
      <button
        className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={handleToggle}
        title="Notifications"
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <h3>Notifications</h3>

            <div className="notification-panel-actions">
              {unreadCount > 0 && (
                <button
                  className="panel-action-btn"
                  onClick={handleMarkAllAsRead}
                  title="Mark all as read"
                >
                  Mark all read
                </button>
              )}

              {notifications.length > 0 && (
                <>
                  {!showClearConfirm ? (
                    <button
                      className="panel-action-btn clear-btn"
                      onClick={handleClearAll}
                      title="Clear all notifications"
                    >
                      Clear all
                    </button>
                  ) : (
                    <div className="clear-confirm">
                      <button
                        className="panel-action-btn confirm-clear-btn"
                        onClick={handleClearAll}
                      >
                        Confirm
                      </button>
                      <button
                        className="panel-action-btn cancel-clear-btn"
                        onClick={handleCancelClear}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="notification-panel-content">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="no-notifications-icon">🔕</span>
                <p>No notifications yet</p>
                <small>You'll see updates and alerts here</small>
              </div>
            ) : (
              <>
                {unreadNotifications.length > 0 && (
                  <div className="notification-section">
                    <div className="notification-section-header">
                      <h4>New</h4>
                      <span className="notification-count">{unreadNotifications.length}</span>
                    </div>

                    <div className="notification-list">
                      {unreadNotifications.map(notification => (
                        <Notification
                          key={notification.id}
                          notification={notification}
                          userId={userId}
                          currentUser={currentUser}
                          onDismiss={handleNotificationDismiss}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {readNotifications.length > 0 && (
                  <div className="notification-section">
                    <div className="notification-section-header">
                      <h4>Earlier</h4>
                    </div>

                    <div className="notification-list">
                      {readNotifications.map(notification => (
                        <Notification
                          key={notification.id}
                          notification={notification}
                          userId={userId}
                          currentUser={currentUser}
                          onDismiss={handleNotificationDismiss}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-panel-footer">
              <small>{notifications.length} notification{notifications.length !== 1 ? 's' : ''}</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
